import { useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import { getPracticePageData, createPracticeSession } from "utils/supabase";

// components

import ButtonGroupNumPreguntas from "components/Custom/ButtonGroupNumPreguntas";
import RadioGroupModo from "components/Custom/RadioGroupModo";
import ButtonGroupDificultad from "components/Custom/ButtonGroupDificultad";
import CheckGroupMaterias from "components/Custom/CheckGroupMaterias";
import BackdropAlert from "components/Custom/BackdropAlert.js";
import ProgresoGeneral from "components/Custom/ProgresoGeneral.js";
import { Brain, ArrowRight } from "lucide-react";
import CardDesempenoEspecialidad from "components/Cards/CardDesempenoEspecialidad";
import RecomendacionesReforzar from "components/Custom/RecomendacionesReforzar";

export default function Practica() {
  const history = useHistory();
  const [numPreguntas, setNumPreguntas] = useState(0);
  const [modo, setModo] = useState("materias");
  const [dificultad, setDificultad] = useState(null);
  const [materiasSeleccionadas, setMateriasSeleccionadas] = useState([]);
  const [practicePageData, setPracticePageData] = useState(null);
  const [backdropAlert, setBackdropAlert] = useState({
    open: false,
    severity: "info",
    title: "",
    message: "",
    description: "",
    closeOnBackdropClick: true,
    primaryAction: null,
    secondaryAction: null,
  });
  const opcionesNumeroPreguntas = [0, 10, 20, 30, 50];
  const mostrarModoEnarm = false;
  const mostrarModoSoloCasosClinicos = false;
  const mostrarModoCasosConImagenes = false;

  const allCatalogSpecialtyIds = useMemo(
    () => (practicePageData?.catalog?.specialties || []).map((specialty) => specialty?.id).filter(Boolean),
    [practicePageData]
  );

  const desempeñoEspecialidadItems = useMemo(() => {
    if (!practicePageData) return null;

    const specialtiesWithQuestions = (practicePageData?.catalog?.specialties || [])
      .filter((specialty) => Number(specialty?.question_count) > 0)
      .map((specialty) => ({
        specialty_id: specialty?.id,
        specialty_name: specialty?.name || "Sin nombre",
        specialty_section: specialty?.section || "Sin sección",
      }));

    const performanceById = new Map(
      (Array.isArray(practicePageData?.performance_by_specialty)
        ? practicePageData.performance_by_specialty
        : []
      )
        .filter((item) => item?.specialty_id)
        .map((item) => [item.specialty_id, item])
    );

    return specialtiesWithQuestions.map((specialty) => {
      const performance = performanceById.get(specialty.specialty_id);
      const totalAnswered = Number(performance?.total_answered ?? 0);
      const correctAnswers = Number(performance?.correct_answers ?? 0);
      const accuracyPercentage = Number(
        performance?.accuracy_percentage ?? performance?.score_percentage ?? 0
      );
      const normalizedAccuracyPercentage = Number.isFinite(accuracyPercentage)
        ? accuracyPercentage > 0 && accuracyPercentage <= 1
          ? accuracyPercentage * 100
          : accuracyPercentage
        : 0;

      return {
        label: specialty.specialty_name,
        specialty_section: performance?.specialty_section || specialty.specialty_section,
        percentage: normalizedAccuracyPercentage,
        correct_answers: Number.isFinite(correctAnswers) ? correctAnswers : 0,
        total_questions: Number.isFinite(totalAnswered) && totalAnswered > 0 ? totalAnswered : undefined,
      };
    });
  }, [practicePageData]);

  const isFormComplete =
    Number.isFinite(Number(numPreguntas)) &&
    Number(numPreguntas) >= 0 &&
    modo &&
    materiasSeleccionadas.length > 0;

  const closeBackdropAlert = () => {
    setBackdropAlert((prev) => ({ ...prev, open: false }));
  };

  const showBackdropAlert = ({
    severity = "info",
    title = "",
    message = "",
    description = "",
    closeOnBackdropClick = true,
    primaryAction = null,
    secondaryAction = null,
  }) => {
    setBackdropAlert({
      open: true,
      severity,
      title,
      message,
      description,
      closeOnBackdropClick,
      primaryAction,
      secondaryAction,
    });
  };

  useEffect(() => {
    async function fetchPracticePageData() {
      try {
        const { data, error } = await getPracticePageData();
        if (error) {
          console.error('Error fetching practice page data:', error);
        } else {
          setPracticePageData(data);
        }
      } catch (error) {
        console.error('Unexpected error fetching practice page data:', error);
      }
    }

    fetchPracticePageData();
  }, []);

  const handleCrearPractica = async () => {
    if (!isFormComplete) {
      showBackdropAlert({
        severity: "warning",
        title: "Faltan campos",
        message: "Por favor, completa todos los campos antes de iniciar la practica.",
        primaryAction: {
          label: "Entendido",
          onClick: closeBackdropAlert,
        },
      });
      return;
    }

    try {
      const selectedIds = Array.isArray(materiasSeleccionadas) ? materiasSeleccionadas : [];
      const hasAllSelected =
        allCatalogSpecialtyIds.length > 0 &&
        allCatalogSpecialtyIds.every((id) => selectedIds.includes(id));

      const requestedTotalQuestions = Number.isFinite(Number(numPreguntas))
        ? Math.max(0, Math.trunc(Number(numPreguntas)))
        : 0;
      const isInfinite = requestedTotalQuestions === 0;

      // Keep arrays/numbers normalized so session settings never store scalars unexpectedly.
      const specialtyIdsForSession = hasAllSelected && isInfinite ? [] : selectedIds;
      const totalQuestionsForSession = requestedTotalQuestions;
      const includeImages = modo === "casos_con_imagenes";

      const { data: practiceSession, error } = await createPracticeSession(
        specialtyIdsForSession,
        totalQuestionsForSession,
        dificultad,
        includeImages,
        modo === "solo_casos_clinicos",
        false,
        40
      );

      if (error) {
        throw error;
      }

      const createdSession = Array.isArray(practiceSession) ? practiceSession[0] : practiceSession;
      const createdSessionId =
        createdSession?.study_session_id ||
        createdSession?.session_id ||
        createdSession?.id ||
        null;

      if (!createdSessionId) {
        throw new Error("No se pudo identificar la sesión creada.");
      }

      try {
        const sessionFlagKey = `practice_session_infinite_${createdSessionId}`;
        const sessionFlagValue = isInfinite ? "1" : "0";

        sessionStorage.setItem(sessionFlagKey, sessionFlagValue);
        localStorage.setItem(sessionFlagKey, sessionFlagValue);
      } catch (storageError) {
        console.warn("No se pudo guardar flag de modo infinito en sessionStorage/localStorage.", storageError);
      }

      const sessionPath = `/admin/practica/${createdSessionId}`;
      const sessionUrl = new URL(sessionPath, window.location.origin);
      if (isInfinite) {
        sessionUrl.searchParams.set("infinite", "1");
      } else {
        sessionUrl.searchParams.delete("infinite");
      }

      history.push(`${sessionUrl.pathname}${sessionUrl.search}`, { isInfinite });
    } catch (error) {
      console.error("Error creating practice exam:", error);
      showBackdropAlert({
        severity: "error",
        title: "No se pudo crear la practica",
        message: error?.message || "Ocurrio un error inesperado.",
        primaryAction: {
          label: "Cerrar",
          onClick: closeBackdropAlert,
        },
      });
    }
  };

  return (
    <>
      <div className="flex flex-wrap mt-100 items-start">
        <div className="w-full lg:w-8/12 px-4 bg-white shadow-md rounded-lg border border-blue-100 p-4 flex flex-col">
          <div className="w-full mb-4">
            <h1 className="text-lg font-bold mb-2 flex items-center text-blue-900">
              <Brain size={24} color="#2563eb" className="mr-2" />Practica inteligente
            </h1>
            <p className="text-blue-700 text-xs mb-4">
              Configura tu practica inteligente a tu medida.
            </p>
          </div>

          <div className="flex flex-col gap-4 md:flex-row">
            <div className="w-full mb-6 xl:mb-0 flex flex-col md:w-1/2 md:mr-4">
              <ButtonGroupNumPreguntas
                value={numPreguntas}
                onChange={setNumPreguntas}
                modo={modo}
                options={opcionesNumeroPreguntas}
                enarmValue={140}
                accentColor="#2563eb"
                accentHoverColor="#1d4ed8"
              />
            </div>
            <div className="w-full mb-6 xl:mb-0 flex flex-col md:w-1/2">
              <ButtonGroupDificultad
                value={dificultad}
                onChange={setDificultad}
                modo={modo}
                accentColor="#2563eb"
                accentHoverColor="#1d4ed8"
              />
            </div>
          </div>

          <div className="flex flex-col gap-4 md:flex-row mt-6">
            <div className="w-full mb-6 xl:mb-0 flex flex-col md:mr-4">
              <RadioGroupModo
                value={modo}
                onChange={setModo}
                showAleatorioOption={false}
                showEnarmOption={mostrarModoEnarm}
                showSoloCasosClinicosOption={mostrarModoSoloCasosClinicos}
                showCasosConImagenesOption={mostrarModoCasosConImagenes}
                accentColor="#2563eb"
              />
            </div>
          </div>

          <div className="flex flex-col gap-4 md:flex-row mt-6">
            <div className="w-full mb-6 xl:mb-0 flex flex-col md:mr-4">
              <CheckGroupMaterias
                onChange={setMateriasSeleccionadas}
                modo={modo}
                defaultSelectAll
                accentColor="#2563eb"
                accentBorderColor="#93c5fd"
                accentHoverBgColor="#eff6ff"
              />
            </div>
          </div>

          <div className="flex flex-col gap-4 md:flex-row mt-6">
            <div className="w-full mb-6 xl:mb-0 flex justify-end md:w-full md:mr-4">
              <button
                className="bg-blue-600 text-white text-sm px-4 py-3 rounded hover:bg-blue-700 transition duration-300 inline-flex items-center justify-center gap-3 leading-none"
                onClick={handleCrearPractica}
              >
                Iniciar practica
                <ArrowRight size={16} className="ml-1" />
              </button>
            </div>
          </div>
        </div>
        <div className="hidden lg:block lg:w-4/12 px-4">
          <div className="relative flex flex-col min-w-0 break-words w-full">
                <div>
                    <ProgresoGeneral data={practicePageData} />
                </div>
                <div className="mt-4">
                  <RecomendacionesReforzar data={practicePageData} recommendations={practicePageData?.recommendations} />
                </div>
                <div className="mt-4">
                  <CardDesempenoEspecialidad items={desempeñoEspecialidadItems} />
                </div>
              </div>
          </div>
      </div>
      <BackdropAlert
        open={backdropAlert.open}
        onClose={closeBackdropAlert}
        severity={backdropAlert.severity}
        title={backdropAlert.title}
        message={backdropAlert.message}
        description={backdropAlert.description}
        closeOnBackdropClick={backdropAlert.closeOnBackdropClick}
        primaryAction={backdropAlert.primaryAction}
        secondaryAction={backdropAlert.secondaryAction}
      />
    </>
  );
}
